import { motion } from 'framer-motion';
import { springs, staggerContainer, staggerItem } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import WavyDivider from '../../components/decorations/WavyDivider';
import type { MindoraMood } from '../../components/character/MindoraCharacter';

interface Props {
  onNext: () => void;
}

const steps: { number: number; color: string; mood: MindoraMood; title: string; description: string }[] = [
  {
    number: 1,
    color: 'var(--kid-ocean)',
    mood: 'idle',
    title: 'You set it up',
    description: 'Choose subjects, set time limits, stay in control.',
  },
  {
    number: 2,
    color: 'var(--kid-violet)',
    mood: 'encouraging',
    title: 'Companion guides them',
    description: "A friendly AI buddy makes learning feel like play.",
  },
  {
    number: 3,
    color: 'var(--kid-meadow)',
    mood: 'happy',
    title: 'You see progress',
    description: "Real-time insights into what they're learning.",
  },
];

export default function WelcomeHowItWorksScreen({ onNext }: Props) {
  const reduced = useReducedMotion();

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12">
      <motion.h2
        className="font-display text-kid-2xl text-kid-text text-center mb-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
      >
        Three simple steps
      </motion.h2>

      <WavyDivider color="var(--kid-ocean)" className="w-48 mb-8" />

      {/* Vertical timeline */}
      <motion.div
        className="flex flex-col items-center gap-0 max-w-sm w-full mb-10"
        variants={staggerContainer(0.15)}
        initial="initial"
        animate="animate"
      >
        {steps.map((step, i) => (
          <motion.div key={i} variants={staggerItem} className="flex flex-col items-center w-full">
            {/* Step */}
            <div className="flex items-start gap-4 w-full">
              {/* Number circle */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center font-display text-kid-xl text-white shadow-kid"
                  style={{ backgroundColor: step.color }}
                >
                  {step.number}
                </div>
              </div>

              {/* Content + Character */}
              <div className="flex items-center gap-3 flex-1 pb-2">
                <div className="flex-1">
                  <h3 className="font-display text-kid-lg text-kid-text mb-1">
                    {step.title}
                  </h3>
                  <p className="text-kid-text-soft font-kid text-kid-sm">
                    {step.description}
                  </p>
                </div>
                <MindoraCharacter mood={step.mood} size="sm" />
              </div>
            </div>

            {/* Dashed connecting line */}
            {i < steps.length - 1 && (
              <div className="flex items-center justify-start w-full pl-[22px]">
                <div
                  className="w-0.5 h-8 opacity-30"
                  style={{
                    backgroundImage: `repeating-linear-gradient(to bottom, ${step.color} 0px, ${step.color} 4px, transparent 4px, transparent 8px)`,
                  }}
                />
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>

      <AnimatedButton
        variant="marshmallow"
        className="btn-marshmallow-meadow"
        onClick={onNext}
      >
        One more thing...
      </AnimatedButton>
    </div>
  );
}
